import React from 'react';
import SarasPopup from '../../../../../library/container/popup/SarasPopup';
import SarasImage from '../../../../../library/input/image/SarasImage';
import SarasTooltip from '../../../../../library/notifications/SarasTooltip';
import SarasTrans from '../../../../../library/notifications/SarasTrans';
import useViewSwitcher from '../../../../../library/hooks/switch/useViewSwitcher';
import SarasViewSwitch from '../../../../../library/hooks/switch/SarasViewSwitch';
import { getShowDocumentLink, getSizedImageUrl } from '../../../../../helper/uiFunctions';
import * as fn from '../../../../../helper/uiFunctions';
import { Icon } from '../../../../../library/decor/SarasIcons';
import { fetchProdPlanAuditDetail, fetchProdPlanOrderSheetDetail, fetchProdPlanProcessDetail } from './prodFunctions';
import ProdPlanPopupProcessData from './ProdPlanPopupProcessData';
import ProdPlanPopupAuditData from './ProdPlanPopupAuditData';
import ProdPlanPopupOrderSheetData from './ProdPlanPopupOrderSheetData';
import SarasDatePicker from '../../../../../library/input/datePicker/SarasDatePicker';

const parseStyle = (styleText, fallback) => {
    if (!styleText) return fallback;

    try {
        const parsed = JSON.parse(styleText);
        return {
            ...fallback,
            ...parsed
        };
    }
    catch (error) {
        return fallback;
    }
};

const buildInfoRows = (viewData) => {
    const createdBy = viewData?.createdBy || '';
    const createdOn = viewData?.createdOn || '';
    const createdValue = createdBy && createdOn
        ? `${createdBy} / ${createdOn}`
        : (createdBy || createdOn);

    return [
        { label: 'Required', value: viewData?.requiredDate || '' },
        { label: 'Created', value: createdValue }
    ].filter((row) => row.value);
};

const trimDateWithoutYear = (dateText) => {
    if (!dateText || typeof dateText !== 'string') return dateText || '';

    const parts = dateText.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[1]}-${parts[2]}`;
    }

    return dateText;
};

const getPopupProcessList = (response) => {
    if (response?.data?.mainViewDataV5?.viewData?.processList) {
        return response.data.mainViewDataV5.viewData.processList;
    }

    if (response?.mainViewData?.processList) {
        return response.mainViewData.processList;
    }

    return response?.processList || [];
};

const getPopupAuditList = (response) => {
    if (response?.data?.mainViewDataV5?.viewData?.logList) {
        return response.data.mainViewDataV5.viewData.logList;
    }

    if (response?.mainViewData?.logList) {
        return response.mainViewData.logList;
    }

    return response?.logList || [];
};

const getPopupOrderSheetList = (response) => {
    if (response?.data?.mainViewDataV5?.viewData?.orderSheetList) {
        return response.data.mainViewDataV5.viewData.orderSheetList;
    }

    if (response?.mainViewData?.orderSheetList) {
        return response.mainViewData.orderSheetList;
    }

    return response?.orderSheetList || [];
};

const ProdPlanGridItemPopup = ({
    anchorRef,
    open,
    onClose,
    onMouseEnter,
    onMouseLeave,
    viewData,
    canMoveDate = true,
    onMoveDate
}) => {
    const [popupProcessList, setPopupProcessList] = React.useState([]);
    const [popupAuditList, setPopupAuditList] = React.useState([]);
    const [popupOrderSheetList, setPopupOrderSheetList] = React.useState([]);
    const [isAuditLoaded, setIsAuditLoaded] = React.useState(false);
    const [isAuditLoading, setIsAuditLoading] = React.useState(false);
    const [isOrderSheetLoaded, setIsOrderSheetLoaded] = React.useState(false);
    const [isOrderSheetLoading, setIsOrderSheetLoading] = React.useState(false);
    const detailSwitcher = useViewSwitcher('process');
    const documentCode = viewData?.docCode || viewData?.did || '';
    const documentTypeId = viewData?.dtid || 0;
    const documentId = viewData?.did || 0;
    const imageUrl = getSizedImageUrl(viewData?.itemImage?.url || '', 'M');
    const imageName = viewData?.itemImage?.fileName || documentCode;
    const targetItem = viewData?.targetItem || {};
    const status = viewData?.status || {};
    const holdRowId = viewData?.holdRowId || 0;
    const holdStatus = viewData?.holdStatus || '';
    const holdStatusDays = viewData?.holdStatusDays || 0;
    const holdLabel = `On Hold: ${holdStatusDays}d`;
    const priority = viewData?.priority || {};
    const pendingWithQC = viewData?.pendingWithQC === true || viewData?.pendingWithQC === 1;
    const pendingWithQCLabel = viewData?.pendingWithQCLabel || 'Pending with QC';
    const pendingWithRequestor = viewData?.pendingWithRequestor === true || viewData?.pendingWithRequestor === 1;
    const pendingWithRequestorLabel = viewData?.pendingWithRequestorLabel || 'Pending with Requestor';
    const hasPendingChip = pendingWithQC || pendingWithRequestor;
    const profile = viewData?.profile || {};
    const statistics = viewData?.statistics || [];
    const planFinishDate = viewData?.planFinishDate || '';
    // const trimmedPlanFinishDate = trimDateWithoutYear(planFinishDate);
    const targetDocumentLabel = targetItem.dtName || targetItem.documentTypeName || 'Target';
    const targetDocumentId = targetItem.did || targetItem.documentId || '';
    const targetDocumentTypeId = targetItem.dtid || targetItem.documentTypeId || 0;
    const [heroPlanFinishDate, setHeroPlanFinishDate] = React.useState(planFinishDate);
    const CalendarIcon = Icon.daterange;

    const onOpenJobOrder = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        const documentLink = getShowDocumentLink(documentTypeId, documentId, '', 1, 'prod', 'order');
        if (!documentLink) return;

        window.open(documentLink, '_blank');
    }, [documentTypeId, documentId]);

    const onOpenTargetDocument = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        const documentLink = getShowDocumentLink(targetDocumentTypeId, targetDocumentId, '', 1, '', '');
        if (!documentLink) return;

        window.open(documentLink, '_blank');
    }, [targetDocumentTypeId, targetDocumentId]);

    const onOpenLargeImage = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        const largeImageUrl = getSizedImageUrl(viewData?.itemImage?.url || '', 'L');
        if (!largeImageUrl) return;

        window.open(largeImageUrl, '_LargeImage');
    }, [viewData]);

    const type1StripStyle = parseStyle(targetItem.type1Style, {
        background: '#2f6fed',
        color: '#fff'
    });

    const statusStyle = parseStyle(status.style, {
        background: '#edf2f7',
        color: '#31435d'
    });

    const pendingChipStyle = {
        background: '#23b915',
        color: '#fff'
    };

    const holdChipStyle = {
        background: '#ff1744',
        color: '#fff'
    };

    const infoRows = buildInfoRows(viewData);
    const processList = popupProcessList;
    const logList = popupAuditList;
    const orderSheetList = popupOrderSheetList;
    const hasProcessList = processList.length > 0;
    const hasStatistics = statistics.length > 0;
    const hasLogList = logList.length > 0;
    const hasOrderSheetList = orderSheetList.length > 0;
    const canLoadDetailTabs = documentTypeId > 0 && documentId > 0;

// console.log("log", logList);

    React.useEffect(() => {
        setHeroPlanFinishDate(viewData?.planFinishDate || '');
    }, [viewData?.planFinishDate, open]);

    React.useEffect(() => {
        let isActive = true;

        if (!open) {
            setPopupProcessList([]);
            setPopupAuditList([]);
            setPopupOrderSheetList([]);
            setIsAuditLoaded(false);
            setIsAuditLoading(false);
            setIsOrderSheetLoaded(false);
            setIsOrderSheetLoading(false);
            return undefined;
        }

        if (!documentTypeId || !documentId) {
            return undefined;
        }

        const fetchDetail = async () => {
            try {
                const processResponse = await fetchProdPlanProcessDetail(documentTypeId, documentId);
                if (!isActive) return;

                setPopupProcessList(getPopupProcessList(processResponse));
            }
            catch (error) {
                if (!isActive) return;

                setPopupProcessList([]);
                console.error('Prod plan detail error:', error);
            }
        };

        fetchDetail();

        return () => {
            isActive = false;
        };
    }, [open, documentTypeId, documentId]);

    React.useEffect(() => {
        if (open) {
            detailSwitcher.onUpdateTriggerKey('process');
        }
    }, [open]);

    React.useEffect(() => {
        let isActive = true;

        if (!open || detailSwitcher.triggerKey !== 'audit' || isAuditLoaded || isAuditLoading || !documentTypeId || !documentId) {
            return undefined;
        }

        const fetchAudit = async () => {
            try {
                setIsAuditLoading(true);
                const auditResponse = await fetchProdPlanAuditDetail(documentTypeId, documentId);
                // console.log("audit response", auditResponse);

                setPopupAuditList(getPopupAuditList(auditResponse));
                setIsAuditLoaded(true);
            }
            catch (error) {
                if (!isActive) return;

                setPopupAuditList([]);
                setIsAuditLoaded(true);
                console.error('Prod plan audit detail error:', error);
            }
            finally {
                if (isActive) {
                    setIsAuditLoading(false);
                }
            }
        };

        fetchAudit();

        return () => {
            isActive = false;
        };
    }, [open, detailSwitcher.triggerKey, isAuditLoaded, isAuditLoading, documentTypeId, documentId]);

    React.useEffect(() => {
        let isActive = true;

        if (!open || detailSwitcher.triggerKey !== 'orderSheet' || isOrderSheetLoaded || isOrderSheetLoading || !documentTypeId || !documentId) {
            return undefined;
        }

        const fetchOrderSheet = async () => {
            try {
                setIsOrderSheetLoading(true);
                const orderSheetResponse = await fetchProdPlanOrderSheetDetail(documentTypeId, documentId);
                
                // console.log("order sheet response", orderSheetResponse, getPopupOrderSheetList(orderSheetResponse));

                setPopupOrderSheetList(getPopupOrderSheetList(orderSheetResponse));
                setIsOrderSheetLoaded(true);
            }
            catch (error) {
                if (!isActive) return;

                setPopupOrderSheetList([]);
                setIsOrderSheetLoaded(true);
                console.error('Prod plan order sheet detail error:', error);
            }
            finally {
                if (isActive) {
                    setIsOrderSheetLoading(false);
                }
            }
        };

        fetchOrderSheet();

        return () => {
            isActive = false;
        };
    }, [open, detailSwitcher.triggerKey, isOrderSheetLoaded, isOrderSheetLoading, documentTypeId, documentId]);

    const onMoveDateChanged = async (sender, nextDateValue) => {
        if (!onMoveDate) {
            return;
        }

        const previousDateValue = heroPlanFinishDate;
        setHeroPlanFinishDate(nextDateValue || '');

        const response = await onMoveDate({
            dtid: documentTypeId,
            did: documentId,
            planDate: nextDateValue
        });

        if (!response || response?.data?.isError) {
            setHeroPlanFinishDate(previousDateValue);
        }
    };

    const onLockedMoveDateClick = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!onMoveDate) {
            return;
        }

        await onMoveDate({
            dtid: documentTypeId,
            did: documentId,
            planDate: heroPlanFinishDate
        });
    };

    const renderHero = () => (
        <div className='uiProdPlanPopupHero'>
            <SarasImage
                src={imageUrl}
                alt={imageName}
                height='112px'
                width='112px'
                onClick={onOpenLargeImage}
            />

            <div className='uiProdPlanPopupHeroBody'>
                <button type='button' className='uiProdPlanPopupDocCodeLink' onClick={onOpenJobOrder}>
                    <span className='uiLink'>{documentCode}</span>
                </button>
                {(viewData?.qty || heroPlanFinishDate || (onMoveDate && canMoveDate)) ? (
                    <div className='uiProdPlanPopupPrimaryMeta'>
                        {viewData?.qty ? (
                            <SarasTooltip title={`Quantity: ${viewData.qty}`} tooltipAlign='bottom-start'>
                                <span className='uiProdPlanPopupPrimaryMetaItem uiProdPlanPopupPrimaryMetaQty'>
                                    {`Qty ${viewData.qty}`}
                                </span>
                            </SarasTooltip>
                        ) : null}

                        {heroPlanFinishDate ? (
                            <SarasTooltip title={`Planned Finish: ${heroPlanFinishDate}`} tooltipAlign='bottom-start'>
                                <span className='uiProdPlanPopupPrimaryMetaItem uiProdPlanPopupPrimaryMetaDate'>
                                    {trimDateWithoutYear(heroPlanFinishDate)}
                                </span>
                            </SarasTooltip>
                        ) : null}

                        {onMoveDate && canMoveDate ? (
                            <SarasDatePicker
                                id='planPopupMoveDate'
                                className='uiHideMuiDatepickerTxt'
                                style={{ width: '50px' }}
                                value={heroPlanFinishDate || null}
                                onChange={onMoveDateChanged}
                            />
                        ) : null}

                        {onMoveDate && !canMoveDate ? (
                            <button
                                type='button'
                                className='uiProdPlanPopupMoveDateLocked'
                                onClick={onLockedMoveDateClick}
                                title='Unlock the plan to move date'
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    padding: '0',
                                    border: '1px solid rgba(49, 67, 93, 0.14)',
                                    borderRadius: '8px',
                                    background: '#f4f7fb',
                                    color: '#7d8aa0',
                                    cursor: 'pointer'
                                }}
                            >
                                <CalendarIcon style={{ fontSize: '18px' }} />
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {profile.name ? (
                    <div className='uiProdPlanPopupCustomer'>
                        <span className='uiProdPlanPopupCustomerLabel'>Customer</span>
                        <span className='uiProdPlanPopupCustomerValue'>{profile.name}</span>
                    </div>
                ) : null}
                {targetDocumentId ? (
                    <button type='button' className='uiProdPlanPopupSubDoc' onClick={onOpenTargetDocument}>
                        <span className='uiProdPlanPopupSubDocLabel'>{targetDocumentLabel}</span>
                        <span className='uiProdPlanPopupSubDocValue'>{targetDocumentId}</span>
                    </button>
                ) : null}
                <SarasTooltip title={targetItem.name || ''} tooltipAlign='bottom-start'>
                    <div className='uiProdPlanPopupItemName'>
                        {targetItem.name || ''}
                    </div>
                </SarasTooltip>

                <div className='uiProdPlanPopupBadges'>
                    {targetItem.type1 ? (
                        <span className='uiProdPlanPopupBadge' style={type1StripStyle}>
                            {targetItem.type1}
                        </span>
                    ) : null}

                    {targetItem.type2 ? (
                        <span className='uiProdPlanPopupBadge uiProdPlanPopupBadgeType2'>
                            {targetItem.type2}
                        </span>
                    ) : null}

                    {priority.name ? (
                        <span className={`uiProdPlanPopupBadge ${priority.class || ''}`}>
                            {priority.name}
                        </span>
                    ) : null}

                    {pendingWithQC ? (
                        <SarasTooltip title={pendingWithQCLabel} tooltipAlign='bottom-start'>
                            <span className='uiProdPlanPopupBadge' style={pendingChipStyle}>
                                PWQ
                            </span>
                        </SarasTooltip>
                    ) : null}

                    {pendingWithRequestor ? (
                        <SarasTooltip title={pendingWithRequestorLabel} tooltipAlign='bottom-start'>
                            <span className='uiProdPlanPopupBadge' style={pendingChipStyle}>
                                PWR
                            </span>
                        </SarasTooltip>
                    ) : null}

                    {!hasPendingChip && !fn.compareNumber(holdRowId, 0) ? (
                        <SarasTooltip title={
                            <>
                                <div><SarasTrans value='Reason' />: <SarasTrans value={holdStatus} /></div>
                                {
                                    !fn.compareNumber(holdStatusDays, 0)
                                    ? <div><SarasTrans value='Since' />: {holdStatusDays} <SarasTrans value='days' /></div>
                                    : null
                                }
                            </>
                        } tooltipAlign='bottom-start'>
                            <span className='uiProdPlanPopupBadge uiChipRed' style={holdChipStyle}>
                                <SarasTrans value={holdLabel} />
                            </span>
                        </SarasTooltip>
                    ) : null}

                    {!hasPendingChip && fn.compareNumber(holdRowId, 0) && status.name ? (
                        <span className='uiProdPlanPopupBadge' style={statusStyle}>
                            {status.name}
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );

    const renderInfoSection = () => (
        <div className='uiProdPlanPopupSection'>
            <div className='uiProdPlanPopupInfoGrid'>
                {infoRows.map((row) => (
                    <div key={row.label} className='uiProdPlanPopupInfoRow'>
                        <div className='uiProdPlanPopupInfoLabel'>{row.label}</div>
                        <div className={`uiProdPlanPopupInfoValue ${row.highlight ? 'uiProdPlanPopupInfoValueHighlight' : ''}`}>{row.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderOrderSheetSection = () => {
        if (!hasOrderSheetList) {
            return (
                <div className='uiProdPlanPopupEmptyState'>
                    No linked order sheet found
                </div>
            );
        }

        return (
            <div>
                {orderSheetList.map((orderSheet, index) => (
                    <ProdPlanPopupOrderSheetData
                        key={`${orderSheet.orderSheetId || 'os'}_${index}`}
                        orderSheet={orderSheet}
                    />
                ))}
            </div>
        );
    };

    const renderSectionSwitch = () => {
        if (!hasStatistics && !canLoadDetailTabs) {
            return null;
        }

        const renderSwitchTrigger = (ctrlKey, label) => {
            const isActive = detailSwitcher.triggerKey === ctrlKey;

            return (
                <button
                    type='button'
                    onClick={() => detailSwitcher.onUpdateTriggerKey(ctrlKey)}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: isActive ? 700 : 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: isActive ? '#243b63' : '#7f8faa',
                        borderBottom: isActive ? '2px solid #243b63' : '2px solid transparent'
                    }}
                >
                    {label}
                </button>
            );
        };

        return (
            <div className='uiProdPlanPopupSection'>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
                    {renderSwitchTrigger('process', `Current ${hasProcessList ? ` (${processList.length})` : ''}`)}
                    {renderSwitchTrigger('orderSheet', `Order Sheet${hasOrderSheetList ? ` (${orderSheetList.length})` : ''}`)}
                    {renderSwitchTrigger('stats', 'Progress')}
                    {renderSwitchTrigger('audit', `Audit${hasLogList ? ` (${logList.length})` : ''}`)}
                </div>

                <SarasViewSwitch viewSwitcher={detailSwitcher} ctrlKey='process' hideMode='soft'>
                    <div>
                        {hasProcessList ? (
                            processList.map((process, index) => (
                                <ProdPlanPopupProcessData
                                    key={`${process.taskTitle || 'process'}_${index}`}
                                    process={process}
                                />
                            ))
                        ) : (
                            <div className='uiProdPlanPopupEmptyState'>
                                No active process found
                            </div>
                        )}
                    </div>
                </SarasViewSwitch>

                <SarasViewSwitch viewSwitcher={detailSwitcher} ctrlKey='stats' hideMode='soft'>
                    <div className='uiProdPlanPopupStatGrid'>
                        {statistics.map((stat, index) => (
                            <div
                                key={`${stat.key || 'stat'}_${index}`}
                                className='uiProdPlanPopupStatCard'
                                title={stat.tooltip || ''}
                            >
                                <div className='uiProdPlanPopupStatKey'>{stat.key || ''}</div>
                                <div className='uiProdPlanPopupStatValue'>{stat.valF || stat.valueF || stat.value || ''}</div>
                            </div>
                        ))}
                    </div>
                </SarasViewSwitch>

                <SarasViewSwitch viewSwitcher={detailSwitcher} ctrlKey='orderSheet' hideMode='soft'>
                    {renderOrderSheetSection()}
                </SarasViewSwitch>

                <SarasViewSwitch viewSwitcher={detailSwitcher} ctrlKey='audit' hideMode='soft'>
                    <div>
                        {hasLogList ? (
                            logList.map((log, index) => (
                                <ProdPlanPopupAuditData
                                    key={`${log.logId || 'log'}_${index}`}
                                    audit={log}
                                />
                            ))
                        ) : (
                            <div className='uiProdPlanPopupEmptyState'>
                                No audit found
                            </div>
                        )}
                    </div>
                </SarasViewSwitch>
            </div>
        );
    };

    return (
        <SarasPopup
            anchorRef={anchorRef}
            open={open}
            onClose={onClose}
            placement='auto-start'
            paperStyle={{
                width: '392px',
                maxWidth: 'min(392px, 90vw)',
                borderRadius: '10px',
                overflow: 'auto',
                maxHeight: 'calc(100vh - 24px)',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.10)'
            }}
        >
            <div
                className='uiProdPlanPopup'
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={(event) => event.stopPropagation()}
            >
                {renderHero()}
                {renderInfoSection()}
                {renderSectionSwitch()}
            </div>
        </SarasPopup>
    );
};

export default ProdPlanGridItemPopup;
